import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useActiveOrg, useAuthStore } from '@/stores/auth';
import { Card } from '@/features/common/ui';

declare const __APP_COMMIT__: string;
declare const __APP_BUILT_AT__: string;

interface Health {
  build: { builtAt: string; commit: string | null } | null;
}

/**
 * Which build is running - the commit the deploy page shows - for the people
 * who redeploy: an admin of this book, or platform staff. Two numbers, not
 * one: the app in this browser and the server it talks to. They differ for
 * exactly one reason, a browser still holding the previous deploy's app,
 * and then the card says to refresh.
 */
export function VersionCard() {
  const org = useActiveOrg();
  const user = useAuthStore((s) => s.user);
  const visible = org?.role === 'admin' || !!user?.isSuperadmin;
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<Health>('/health'),
    enabled: visible,
    staleTime: 60_000,
  });
  if (!visible) return null;

  const server = health.data?.build?.commit ?? null;
  const app = typeof __APP_COMMIT__ === 'string' && __APP_COMMIT__ ? __APP_COMMIT__ : null;
  const builtAt = health.data?.build?.builtAt;
  const stale = !!server && !!app && server !== app;

  return (
    <Card className="p-4" data-testid="version-card">
      <h2 className="text-sm font-semibold">Version</h2>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Server</dt>
        <dd className="font-mono" data-testid="version-server">
          {server ?? (health.isLoading ? '…' : 'unknown')}
          {builtAt ? <span className="ml-2 font-sans text-xs text-muted-foreground">built {new Date(builtAt).toLocaleString('en-IN')}</span> : null}
        </dd>
        <dt className="text-muted-foreground">This app</dt>
        <dd className="font-mono" data-testid="version-app">
          {app ?? 'dev'}
          {typeof __APP_BUILT_AT__ === 'string' && __APP_BUILT_AT__ ? (
            <span className="ml-2 font-sans text-xs text-muted-foreground">built {new Date(__APP_BUILT_AT__).toLocaleString('en-IN')}</span>
          ) : null}
        </dd>
      </dl>
      {stale ? (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300" data-testid="version-stale">
          This browser is running an older copy of the app than the server. Refresh the page to load {server}.
        </p>
      ) : null}
    </Card>
  );
}
